import { auth } from '../firebase';

export const getToken = (): string | null => {
  // Prefer localStorage (remember me), fall back to sessionStorage
  const token = localStorage.getItem('token');
  if (token) return token;
  return sessionStorage.getItem('token');
};

// Resolve the Firebase auth session once on first use (it restores async after a
// page reload), so getFreshToken can mint a current idToken right away.
let _authReady: Promise<void> | null = null;
function waitForAuth(): Promise<void> {
  if (_authReady) return _authReady;
  _authReady = new Promise<void>((resolve) => {
    try {
      const unsub = auth.onAuthStateChanged(() => { unsub(); resolve(); });
      setTimeout(resolve, 2500); // don't hang if auth never initialises
    } catch { resolve(); }
  });
  return _authReady;
}

/**
 * A guaranteed-fresh token. If a live Firebase Auth session exists it mints a
 * current idToken (and re-stores it); otherwise it falls back to the stored token.
 * Fixes the "still logged in but every admin fetch 401s → dashboard shows no data"
 * case, where the stored idToken had expired or gone missing.
 */
export const getFreshToken = async (): Promise<string | null> => {
  try {
    await waitForAuth();
    const u = auth.currentUser;
    if (u) {
      const t = await u.getIdToken();
      if (t) { try { localStorage.setItem('token', t); } catch { /* ignore */ } return t; }
    }
  } catch { /* fall through to stored token */ }
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

export const getAuthTokens = (): any | null => {
  const tokensStr = localStorage.getItem('authTokens') || sessionStorage.getItem('authTokens');
  if (!tokensStr) return null;
  try {
    return JSON.parse(tokensStr);
  } catch (err) {
    return null;
  }
};

export const getUser = (): any | null => {
  const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch (err) {
    return null;
  }
};

export const setAuth = (user: any, tokens: any, remember = true) => {
  const storage = remember ? localStorage : sessionStorage;
  try {
    storage.setItem('user', JSON.stringify(user));
    if (tokens) {
      storage.setItem('authTokens', JSON.stringify(tokens));
      storage.setItem('token', tokens.idToken);
    }
  } catch (err) {
    console.error('Failed to set auth data', err);
  }
};

export const clearAuth = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('authTokens');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('authTokens');
  } catch (err) {
    console.error('Failed to clear auth data', err);
  }
};
